// src/components/Sidebar.jsx
import React from 'react';
// S-304: plan keys are IDENTIFIERS, never labels. '1d' is the 3-Day Pass.
import { planBadge } from '../constants/planLabels';
import { useApp } from '../context/AppContext';
import { auth, clubs as clubsApi } from '../services/api'; // v232 Session C

import { G } from '../constants';
import UserAvatar from './UserAvatar';

const Sidebar = React.memo(function Sidebar() {
  const { user, setUser, page, setPage, sidebar, setSidebar, portfolio, darkMode, bg2, bord, text, tx2, s, toast } = useApp();
  const [showSharePopup, setShowSharePopup] = React.useState(false);

  // v232 Session C: My Clubs quick-access list (refreshes when sidebar opens)
  const [myClubs, setMyClubs] = React.useState([]);
  const [myClubsLoaded, setMyClubsLoaded] = React.useState(false);
  React.useEffect(() => {
    if (!sidebar || !user?.id) return;
    let active = true;
    clubsApi.myClubs()
      .then(r => { if (active && r?.success) setMyClubs(r.clubs || []); })
      .catch(() => {})
      .finally(() => { if (active) setMyClubsLoaded(true); });
    return () => { active = false; };
  }, [sidebar, user?.id]);

  const navItems = [
    ['🏠','Home','home'],
    ['📈','Trade','trade'],
    ['🕯️','Advanced Chart','chart'],
    ['🔬','Trade Lab','tradelab'],
    ['🚀','Top Movers','movers'],
    ['🏆','Leaderboard','leaderboard'],
    ['🛍️','Store','store'],
    ['🏛️','Auction House','auction'],
    // v235: Tournaments removed (B+ plan)
    // P12-16 (2026-05-21): Renamed display label 'Gamification' → 'Rewards' for
    // clearer user-facing terminology. Internal route key 'gamification' kept
    // unchanged to avoid breaking page routing.
    ['🎮','Rewards','gamification'],
    ['📰','News','news'],
    ['🏛️','Forums','forums'],
    ['👤','Profile','profile'],
    ['🔔','Price Alerts','alerts'],
    ['📋','Order History','orders'],
    // P48b (2026-05-26): Added Advanced Orders sidebar link. The page +
    // route have existed since v187 (App.js:60 + 179), but the sidebar
    // entry was missing — users could only reach it via direct URL.
    // Mobile has the equivalent drawer entry ('⚡ Advanced Orders') so this
    // restores full webapp↔mobile parity (Rule Q).
    ['⚡','Advanced Orders','advanced-orders'],
    ['💼','Watchlist','watchlist'],
    ['💰','P&L Report','pnl'],
    ['⭐','Paper Crypto Pro','premium'],
    ['⚙️','Settings','settings'],
    ['🎧','Support','support'],
    ['📤','Share App','__share_popup__'],
    ['📌','Saved Posts','saved'],
    ['🔄','Copy Trading','copytrading'],
    ['ℹ️','About','about'],
    ['📑','Legal & Privacy','legal'],
    ['🎁','Referral Program','__referral_popup__'],
    // S-032: Admin link only visible for users with role='admin' (parity with mobile)
    ...(user?.role === 'admin' ? [['🛡️','Admin Panel','admin']] : []),
  ];

  return (
    <>
      <div onClick={() => setSidebar(false)}
        style={{ position:'fixed', inset:0,
          // v270 (G4.11.1): AdSense compliance — opacity 0.35 (≤0.5 per Better Ad Standards) + blur for production-grade look.
          // Webapp uses AdSense; over-graying violates policy. Same fix applied to share popup below.
          background:'rgba(0,0,0,0.35)', backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)',
          zIndex:150, display:sidebar?'block':'none' }}/>

      <div style={{ position:'fixed', top:0, left:sidebar?0:-290, width:290, height:'100vh', background:bg2, borderRight:`1px solid ${bord}`, zIndex:200, transition:'left 0.3s cubic-bezier(.4,0,.2,1)', overflowY:'auto', display:'flex', flexDirection:'column' }}>

        {/* Profile banner */}
        <div style={{ background:'linear-gradient(135deg,#1A0E00,#2D1E00)', padding:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
            <div onClick={() => { setSidebar(false); setPage('profile'); }} style={{ cursor:'pointer' }}>
              <UserAvatar userId={user.id} username={user.username} size={58}/>
            </div>
            <div>
              <div style={{ fontWeight:800, color:G, fontSize:15 }}>{user.username}</div>
              <div style={{ color:'#888', fontSize:11 }}>{user.email}</div>
              <div style={{ color:'#0ECB81', fontSize:11, marginTop:2 }}>
                {user.plan && user.plan !== 'free' ? `⭐ ${planBadge(user.plan)}` : '🆓 Free Plan'}
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:20 }}>
            {[
              ['$' + (portfolio?.balance_usdt ? Number(portfolio.balance_usdt).toLocaleString(undefined,{maximumFractionDigits:0}) : (user?.portfolio_balance?.toFixed(0) ?? '15000')), 'Balance'],
              ['0', 'Followers'],
              ['0', 'Following'],
            ].map(([v, l]) => (
              <div key={l} style={{ textAlign:'center' }}>
                <div style={{ color:G, fontWeight:700, fontSize:14 }}>{v}</div>
                <div style={{ color:'#888', fontSize:10 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Nav items */}
        <div style={{ padding:8, flex:1 }}>
          {navItems.map(([icon, label, pg]) => (
            <div key={pg} onClick={() => {
                if (pg === '__share_popup__' || pg === '__referral_popup__') { setShowSharePopup(true); setSidebar(false); return; }
                setPage(pg); setSidebar(false);
              }}
              style={{ ...s.navItem, background: page === pg ? `${G}18` : 'transparent', borderRadius:8 }}>
              <span style={{ fontSize:17 }}>{icon}</span>
              <span style={{ color: page === pg ? G : text, fontWeight: page === pg ? 700 : 400, fontSize:14 }}>{label}</span>
            </div>
          ))}

          {/* v232 Session C: My Clubs quick-access list */}
          {user?.id && myClubs.length > 0 && (
            <div style={{ marginTop:14, paddingTop:10, borderTop:`1px solid ${bord}` }}>
              <div style={{ fontSize:10, color:tx2, fontWeight:700, padding:'4px 12px',
                textTransform:'uppercase', letterSpacing:1.2 }}>
                🏟 My Clubs
              </div>
              {myClubs.slice(0, 6).map(c => (
                <div key={c.id} onClick={() => {
                    try { sessionStorage.setItem('pc_club_id', c.id); } catch {}
                    setPage('clubDetail'); setSidebar(false);
                  }}
                  style={{ ...s.navItem, paddingLeft:18, borderRadius:8 }}>
                  <span style={{ fontSize:16 }}>{c.icon || '🏟'}</span>
                  <span style={{ flex:1, fontSize:13,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {c.name}
                  </span>
                  {c.role === 'admin' && (
                    <span style={{ fontSize:9, color:'#4B8BF5', fontWeight:700,
                      background:'#4B8BF515', padding:'1px 5px', borderRadius:4 }}>
                      ADMIN
                    </span>
                  )}
                </div>
              ))}
              {myClubs.length > 6 && (
                <div style={{ padding:'4px 18px', fontSize:10, color:tx2 }}>
                  + {myClubs.length - 6} more
                </div>
              )}
            </div>
          )}

          <div onClick={() => { auth.logout(); setUser(null); setSidebar(false); }}
            style={{ ...s.navItem, borderTop:`1px solid ${bord}`, marginTop:8, paddingTop:16 }}>
            <span style={{ fontSize:17 }}>🚪</span>
            <span style={{ color:'#F6465D', fontWeight:600 }}>Log Out</span>
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{ padding:12, margin:'0 8px 8px', background:darkMode?'#0A0700':'#FFF8D0', borderRadius:8, border:`1px solid ${bord}` }}>
          <div style={{ color:G, fontWeight:700, fontSize:10, marginBottom:3 }}>⚠ DISCLAIMER</div>
          <div style={{ color:tx2, fontSize:10, lineHeight:1.5 }}>Paper Crypto is simulated trading. Virtual funds only. Not financial advice.</div>
        </div>
      </div>

      {/* Referral Share Popup */}
      {showSharePopup && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'flex-end',
          // v270 (G4.11.1): AdSense compliance — opacity 0.35 (≤0.5 per Better Ad Standards) + blur. Was 0.78.
          background:'rgba(0,0,0,0.35)', backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)' }} onClick={() => setShowSharePopup(false)}>
          <div style={{ background:'#111', borderRadius:'20px 20px 0 0', padding:'20px 16px 32px',
            width:'100%', maxWidth:440, margin:'0 auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:900, fontSize:18, color:'#F5C518', marginBottom:4, textAlign:'center' }}>
              🎁 Invite Friends
            </div>
            <div style={{ color:'#888', fontSize:12, textAlign:'center', marginBottom:14 }}>
              Share and earn rewards!
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
              {[
                { icon:'💬', label:'WhatsApp', href:'https://wa.me/?text='+encodeURIComponent('🚀 Try Paper Crypto! https://papercrypto.app') },
                { icon:'𝕏',  label:'Twitter',  href:'https://twitter.com/intent/tweet?text='+encodeURIComponent('🚀 Paper Crypto! https://papercrypto.app') },
                { icon:'📘', label:'Facebook', href:'https://www.facebook.com/sharer/sharer.php?u=https://papercrypto.app' },
                { icon:'📸', label:'Instagram',href:null },
                { icon:'🧵', label:'Threads',  href:'https://www.threads.net/intent/post?text='+encodeURIComponent('https://papercrypto.app') },
                { icon:'📧', label:'Email',    href:'mailto:?subject=Try Paper Crypto!&body=https://papercrypto.app' },
              ].map(b => (
                <button key={b.label}
                  onClick={() => b.href ? window.open(b.href,'_blank') : (navigator.clipboard?.writeText('https://papercrypto.app'), toast?.('Link copied!'))}
                  style={{ padding:'10px 4px', borderRadius:10, border:'1px solid #444',
                    background:'#1A1A1A', color:'#DDD', fontWeight:700, cursor:'pointer',
                    display:'flex', flexDirection:'column', alignItems:'center', gap:5, fontSize:10 }}>
                  <span style={{ fontSize:20 }}>{b.icon}</span>{b.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
);
export default Sidebar;
